/* THE fund selector — the SAME control in every page title (Ale ruling
 * 2026-09-05, Tier-2 "one fund selector everywhere"; the approved shape
 * is the fund Overview's switcher, IA-fund/full/overview.png).
 *
 * The title reads the fund's name with a caret. Open it and the funds
 * stand WITH OPEN ITEMS FIRST, each carrying the count that matters on
 * THIS page — a queue's own figure on a queue, every open item on Today
 * and the Workbench — with the funds that have nothing open in a muted
 * group below. Nothing is ever hidden: a quiet fund is still reachable,
 * it just does not compete with work. A find box narrows long lists, and
 * "All funds ›" is both the default and the clear.
 *
 * KEYBOARD: ⌘K (or /) opens it from anywhere on the page, ↑ ↓ walk the
 * rows, Enter chooses, Esc closes and puts focus back on the title — so
 * the officer never loses their place. Typing in a field never triggers
 * it.
 *
 * It replaces the Workbench fund chips and the record-keeping queue's
 * "switch fund" menu: one control, not three.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { noteFundMenu } from './fundScope'
import './fundselector.css'

export interface SelectorFund {
  id: string
  name: string
  /** The count that matters on THIS page. */
  open: number
  /** The clauses under the name — "1 to decide · 2 breaks · 5 owed". */
  sub?: string | null
}

interface Props {
  funds: SelectorFund[]
  /** The fund in scope, or null = All funds. */
  value: string | null
  onChoose: (id: string | null) => void
  /** The word after the figure — "open" on Today, the queue's own noun
   *  on a queue ("to book", "to reconcile", …). */
  noun?: string
  /** Rendered after the name in the title line: form · jurisdiction. */
  meta?: string | null
  /** Still loading: the title reads the choice, the menu says so. */
  loading?: boolean
}

/** A keystroke aimed at a field is never a shortcut. */
function typingIn(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el || !el.tagName) return false
  const tag = el.tagName.toLowerCase()
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable === true
}

export default function FundSelector({
  funds,
  value,
  onChoose,
  noun = 'open',
  meta = null,
  loading = false,
}: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [cursor, setCursor] = useState(0)
  const rootRef = useRef<HTMLSpanElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const findRef = useRef<HTMLInputElement | null>(null)
  const menuId = useId()

  const chosen = value ? (funds.find((f) => f.id === value) ?? null) : null
  /* A fund IS in scope but its name has not arrived yet (the ledger is
   * still in flight): say so with an ellipsis. Printing "All funds" over
   * a scoped page is the lie the whole ruling exists to end. */
  const label = chosen ? chosen.name : value ? '…' : 'All funds'

  /* Open items FIRST, most open first, then by name; the quiet funds
   * keep their own group below — muted, never hidden. */
  const { working, quiet } = useMemo(() => {
    const q = query.trim().toLowerCase()
    const rows = q ? funds.filter((f) => f.name.toLowerCase().includes(q)) : funds
    const byName = (a: SelectorFund, b: SelectorFund) =>
      a.name.localeCompare(b.name, 'en', { sensitivity: 'base' })
    return {
      working: rows.filter((f) => f.open > 0).sort((a, b) => b.open - a.open || byName(a, b)),
      quiet: rows.filter((f) => f.open <= 0).sort(byName),
    }
  }, [funds, query])

  /* The walk order IS the display order, "All funds" last — the row the
   * ruling makes the clear. */
  const walk = useMemo<(string | null)[]>(
    () => [...working.map((f) => f.id), ...quiet.map((f) => f.id), null],
    [working, quiet]
  )

  const close = useCallback((refocus = true) => {
    setOpen(false)
    setQuery('')
    if (refocus) triggerRef.current?.focus()
  }, [])

  const choose = useCallback(
    (id: string | null) => {
      onChoose(id)
      close()
    },
    [onChoose, close]
  )

  // The menu owns the keyboard while it stands (fundScope.fundMenuOpen).
  useEffect(() => {
    if (!open) return
    noteFundMenu(1)
    return () => noteFundMenu(-1)
  }, [open])

  // Opening lands the cursor on the chosen fund and the caret in the find box.
  useEffect(() => {
    if (!open) return
    const at = walk.indexOf(value)
    setCursor(at >= 0 ? at : 0)
    findRef.current?.focus()
    // walk is intentionally read once, at open: later filtering moves the
    // cursor through the handler below, not by re-seeding it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // ⌘K / ctrl-K / "/" open it from anywhere on the page.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (open) return
      if (typingIn(e.target)) return
      const combo = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k'
      if (!combo && e.key !== '/') return
      e.preventDefault()
      setOpen(true)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  // A click outside closes it — without stealing focus back.
  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) close(false)
    }
    window.addEventListener('mousedown', onDown)
    return () => window.removeEventListener('mousedown', onDown)
  }, [open, close])

  const onMenuKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      close()
      return
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      setCursor((c) => {
        const n = walk.length
        if (n === 0) return 0
        return (c + (e.key === 'ArrowDown' ? 1 : n - 1)) % n
      })
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      choose(walk[Math.min(cursor, walk.length - 1)] ?? null)
    }
  }

  const row = (f: SelectorFund, index: number, muted: boolean) => (
    <button
      type="button"
      key={f.id}
      role="option"
      aria-selected={f.id === value}
      data-testid="fund-selector-row"
      data-fund={f.id}
      data-open={f.open}
      data-quiet={muted || undefined}
      className={`fs-row${muted ? ' quiet' : ''}${index === cursor ? ' cursor' : ''}${
        f.id === value ? ' on' : ''
      }`}
      onMouseEnter={() => setCursor(index)}
      onClick={() => choose(f.id)}
    >
      <span className="fs-tick" aria-hidden="true">
        {f.id === value ? '✓' : ''}
      </span>
      <span className="fs-who">
        <span className="fs-nm">{f.name}</span>
        {f.sub ? <small className="fs-sub">{f.sub}</small> : null}
      </span>
      <span className="fs-n mono" data-testid="fund-selector-count">
        <b>{f.open}</b> {noun}
      </span>
    </button>
  )

  const allIndex = walk.length - 1

  return (
    <span className="fs" ref={rootRef} data-testid="fund-selector" data-open={open || undefined}>
      <button
        type="button"
        ref={triggerRef}
        className={`fs-trigger${open ? ' open' : ''}`}
        data-testid="fund-selector-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={(e) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            setOpen(true)
          }
        }}
      >
        {label}
        <span className="fs-caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {meta ? <span className="fs-meta">{meta}</span> : null}
      {open ? (
        <span
          className="fs-menu"
          id={menuId}
          role="listbox"
          aria-label="Choose a fund"
          data-testid="fund-selector-menu"
          onKeyDown={onMenuKey}
        >
          <span className="fs-find">
            <input
              ref={findRef}
              className="fs-input"
              type="text"
              aria-label="find a fund"
              placeholder="find a fund"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setCursor(0)
              }}
              data-testid="fund-selector-find"
            />
            <span className="fs-kbd mono" aria-hidden="true">
              ⌘K
            </span>
          </span>
          <span className="fs-rows">
            {working.map((f, i) => row(f, i, false))}
            {quiet.length > 0 ? (
              <span className="fs-grp" data-testid="fund-selector-quiet">
                nothing open
              </span>
            ) : null}
            {quiet.map((f, i) => row(f, working.length + i, true))}
            {working.length === 0 && quiet.length === 0 ? (
              <span className="fs-grp" data-testid="fund-selector-none">
                {loading ? 'reading the ledger…' : 'no fund matches'}
              </span>
            ) : null}
          </span>
          <span className="fs-foot">
            <span className="fs-tot">
              {funds.length} fund{funds.length === 1 ? '' : 's'} in the record
            </span>
            <button
              type="button"
              role="option"
              aria-selected={value === null}
              className={`fs-all${cursor === allIndex ? ' cursor' : ''}${value === null ? ' on' : ''}`}
              data-testid="fund-selector-all"
              onMouseEnter={() => setCursor(allIndex)}
              onClick={() => choose(null)}
            >
              All funds ›
            </button>
          </span>
        </span>
      ) : null}
    </span>
  )
}
